/* WAD3-Leser: Verzeichnis lesen, Texturen bei Bedarf in RGBA umwandeln. Reine Logik ohne DOM. */
import I18n from '../i18n.ts';
/** Eintrag im Verzeichnis einer WAD (Mip 0 liegt bei lump) */
export interface WadEntry { name: string; w: number; h: number; lump: number; disk: number }
export interface WadFile { name: string; entries: Map<string, WadEntry>; dv: DataView; size: number }
/** Dekodiertes Bild: RGBA, 4 Bytes je Pixel */
export interface Pixels { w: number; h: number; rgba: Uint8ClampedArray }

const Wad = (function () {
  'use strict';
  const _ = I18n.t;

  const TYPE_MIPTEX = 0x43;

  function readName(dv: DataView, off: number, max: number): string {
    let s = '';
    for (let i = 0; i < max; i++) {
      const c = dv.getUint8(off + i);
      if (!c) break;
      s += String.fromCharCode(c);
    }
    return s;
  }

  /** Liest das Verzeichnis. Liefert { name, entries: Map(NAME -> {name,w,h,lump,dv}) } oder wirft Error. */
  function parse(buffer: ArrayBuffer, fileName?: string): WadFile {
    const dv = new DataView(buffer);
    if (buffer.byteLength < 12) throw new Error(_('wad.e_small'));
    const magic = readName(dv, 0, 4);
    if (magic !== 'WAD3') throw new Error(magic === 'WAD2' ? _('wad.e_wad2') : _('wad.e_not_wad3'));
    const n = dv.getInt32(4, true), tbl = dv.getInt32(8, true);
    if (n < 0 || tbl < 12 || tbl + n * 32 > buffer.byteLength) throw new Error(_('wad.e_dir'));
    const entries = new Map<string, WadEntry>();
    for (let i = 0; i < n; i++) {
      const o = tbl + i * 32;
      const pos = dv.getInt32(o, true), disk = dv.getInt32(o + 4, true), type = dv.getUint8(o + 12);
      const name = readName(dv, o + 16, 16);
      if (type !== TYPE_MIPTEX || !name || pos < 0 || pos + 40 > buffer.byteLength) continue;
      const w = dv.getUint32(pos + 16, true), h = dv.getUint32(pos + 20, true);
      const off0 = dv.getUint32(pos + 24, true);
      if (!w || !h || w > 4096 || h > 4096 || !off0) continue; // extern gespeicherte Texturen haben keine Pixel
      entries.set(name.toUpperCase(), { name: name, w: w, h: h, lump: pos, disk: disk });
    }
    return { name: fileName || 'wad', entries: entries, dv: dv, size: buffer.byteLength };
  }

  /** Dekodiert Mip 0 nach RGBA (Uint8ClampedArray). '{'-Texturen: Index 255 ist transparent. */
  function decode(wad: WadFile, entry: WadEntry): Pixels {
    const dv = wad.dv, base = entry.lump, w = entry.w, h = entry.h;
    const off0 = dv.getUint32(base + 24, true);
    const palOff = base + dv.getUint32(base + 24 + 4 * 3, true) + (w / 8) * (h / 8) + 2;
    const px = base + off0;
    if (px + w * h > dv.byteLength || palOff + 768 > dv.byteLength) throw new Error(_('wad.e_data', { name: entry.name }));
    const out = new Uint8ClampedArray(w * h * 4), trans = entry.name.charAt(0) === '{';
    for (let i = 0; i < w * h; i++) {
      const idx = dv.getUint8(px + i), p = palOff + idx * 3;
      out[i * 4] = dv.getUint8(p); out[i * 4 + 1] = dv.getUint8(p + 1); out[i * 4 + 2] = dv.getUint8(p + 2);
      out[i * 4 + 3] = trans && idx === 255 ? 0 : 255;
    }
    return { w: w, h: h, rgba: out };
  }

  /** Mehrere WADs zu einem Register (spätere überschreiben frühere nicht: erste gewinnt, wie im Spiel). */
  class Registry {
    wads: WadFile[] = [];
    index = new Map<string, { wad: WadFile; entry: WadEntry }>();
    cache = new Map<string, Pixels | null>();
    add(wad: WadFile): void {
      this.wads.push(wad);
      wad.entries.forEach((e, k) => { if (!this.index.has(k)) this.index.set(k, { wad: wad, entry: e }); });
      // Zuvor vergeblich gesuchte Texturen (null im Cache) neu zulassen
      this.cache.forEach((v, k) => { if (v === null) this.cache.delete(k); });
    }
    clear(): void { this.wads = []; this.index = new Map(); this.cache = new Map(); }
    count(): number { return this.index.size; }
    has(name: string): boolean { return this.index.has(String(name).toUpperCase()); }
    size(name: string): { w: number; h: number } | null { const r = this.index.get(String(name).toUpperCase()); return r ? { w: r.entry.w, h: r.entry.h } : null; }
    names(): string[] { return Array.from(this.index.values()).map((r) => r.entry.name).sort((a, b) => a.localeCompare(b)); }
    pixels(name: string): Pixels | null {
      const k = String(name).toUpperCase();
      if (this.cache.has(k)) return this.cache.get(k) as Pixels | null;
      const r = this.index.get(k);
      const d = r ? decode(r.wad, r.entry) : null;
      this.cache.set(k, d);
      return d;
    }
  }

  return { parse: parse, decode: decode, Registry: Registry };
})();
export default Wad;
